/**
 * miD — Contemporary House Construction Loader (sketch edition).
 *
 * Original founder-designed loader (see /tmp/miDHouseLoader.jsx).
 * Progress-driven SVG: a two-volume architect's house builds itself 0-100%.
 *
 * Two exports:
 *   • <MiDHouseLoader progress={n} />       — determinate (0-100)
 *   • <MiDHouseLoaderAuto />                — indeterminate; auto-cycles 0→100
 *     with looping animation. Drop-in replacement for spinners.
 *
 * Brand (v3): ink, purple, teal, silver, paper.
 *
 * With NEW_BRAND on (lib/brand.js) both exports render ZonamentoLoader instead:
 * the logo's three stacked legend squares (styles in ./ui_brand.css).
 */
import React, { useState, useEffect, useRef } from "react";
import { NEW_BRAND, brandColor } from "@/lib/brand";

const phase = (p, s, e) => (p <= s ? 0 : p >= e ? 1 : (p - s) / (e - s));
const easeOut = (t) => 1 - Math.pow(1 - t, 3);
const clamp01 = (t) => Math.max(0, Math.min(1, t));

function HouseLoader({ progress = 0, size = 220, label, scene = true }) {
  const p = Math.max(0, Math.min(100, progress));
  const reduce =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // ponytail: v3 art, only rendered with the switch off; brandColor keeps it literal-guard clean.
  const INK = brandColor("ink", "#1A1B2E"), INK_D = brandColor("ink", "#111223"), INK_L = brandColor("ink", "#2C2E4A"),
        PURPLE = brandColor("primary", "#7E5FB8"), PURPLE_D = brandColor("primary", "#6B4FA0"), PURPLE_L = brandColor("primary", "#9678C9"),
        TEAL_L = brandColor("espacos-verdes-green", "#7DDFD0"), TEAL_G = brandColor("espacos-verdes-green", "#3CC8B5"),
        SILVER = brandColor("line-strong", "#B5B5B5"), PAPER = brandColor("paper", "#FAFAF7");

  const g  = reduce ? (p > 0 ? 1 : 0)   : easeOut(phase(p, 0, 12));
  const a  = reduce ? (p >= 12 ? 1 : 0) : easeOut(phase(p, 12, 34));
  const b  = reduce ? (p >= 34 ? 1 : 0) : easeOut(phase(p, 34, 56));
  const r  = reduce ? (p >= 56 ? 1 : 0) : easeOut(phase(p, 56, 70));
  const gl = reduce ? (p >= 70 ? 1 : 0) : easeOut(phase(p, 70, 86));
  const f  = reduce ? (p >= 86 ? 1 : 0) : easeOut(phase(p, 86, 100));

  const gY = 168;
  const DY = 8;

  const aX = 44, aW = 90, aTop0 = 112, aH = gY - aTop0, aDX = 22;
  const aTop = gY - aH * a;

  const bX = 56, bW = 114, bBot = 112, bTop0 = 66, bH = bBot - bTop0, bDX = 20;
  const bTop = bBot - bH * b;

  const roofDrop = (1 - r) * 5;
  const hatch = clamp01((a - 0.78) / 0.22);
  const hatchB = clamp01((b - 0.78) / 0.22);

  const gxA = 52, gyA = 124, gwA = 64, ghA = gY - gyA;
  const gxB = 64, gyB = 78, gwB = 98, ghB = 20;
  const dx = 118, dy = 138, dw = 15, dh = gY - dy;

  const quad = (pts) => pts.map(([x, y]) => `${x},${y}`).join(" ");
  const line = { stroke: INK, strokeWidth: 0.9, strokeLinejoin: "round", opacity: 0.85 };

  const hatchA = [139, 144, 149, 154].map((x) => {
    const t = (x - (aX + aW)) / aDX;
    return { x, y1: aTop - DY * t, y2: gY - DY * t };
  });
  const hatchLinesB = [174, 179, 184, 189].map((x) => {
    const t = (x - (bX + bW)) / bDX;
    return { x, y1: bTop - DY * t, y2: bBot - DY * t };
  });

  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(p)} aria-valuemin={0} aria-valuemax={100}
      aria-label={label || "A construir o teu Buyer DNA"}
      style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 14 }}
      data-testid="mid-house-loader"
    >
      <svg width={size} height={size} viewBox="0 0 220 220" fill="none">
        <g transform="translate(4,-10)">
          <line x1="18" y1={gY} x2="202" y2={gY} stroke={SILVER} strokeWidth="2.5" strokeLinecap="round"
            strokeDasharray="184" strokeDashoffset={184 * (1 - g)} />
          <g opacity={0.45 * g} stroke={SILVER} strokeWidth="1.2" strokeLinecap="round">
            <line x1="24" y1="175" x2="66" y2="174" />
            <line x1="152" y1="174" x2="196" y2="173" />
          </g>
          {(a > 0 || b > 0) && (
            <ellipse cx="118" cy={gY + 5} rx={84 * Math.max(a, b)} ry="4.5" fill={INK} opacity={0.07 * Math.max(a, b)} />
          )}
          {a > 0 && (
            <g opacity={Math.min(1, a * 1.3)}>
              <polygon points={quad([[aX, aTop], [aX + aDX, aTop - DY], [aX + aW + aDX, aTop - DY], [aX + aW, aTop]])}
                fill={PURPLE_L} {...line} />
              <polygon points={quad([[aX + aW, aTop], [aX + aW + aDX, aTop - DY], [aX + aW + aDX, gY - DY], [aX + aW, gY]])}
                fill={PURPLE_D} {...line} />
              <rect x={aX} y={aTop} width={aW} height={aH * a} fill={PURPLE} {...line} />
              <g opacity={0.16 * hatch} stroke={PAPER} strokeWidth="0.9">
                {hatchA.map((h) => <line key={h.x} x1={h.x} y1={h.y1 + 4} x2={h.x} y2={h.y2 - 3} />)}
              </g>
            </g>
          )}
          {b > 0 && (
            <g opacity={Math.min(1, b * 1.3)}>
              <polygon points={quad([[bX, bTop], [bX + bDX, bTop - DY], [bX + bW + bDX, bTop - DY], [bX + bW, bTop]])}
                fill={INK_L} {...line} />
              <polygon points={quad([[bX + bW, bTop], [bX + bW + bDX, bTop - DY], [bX + bW + bDX, bBot - DY], [bX + bW, bBot]])}
                fill={INK_D} {...line} />
              <rect x={bX} y={bTop} width={bW} height={bH * b} fill={INK} {...line} />
              <g opacity={0.14 * hatchB} stroke={PAPER} strokeWidth="0.9">
                {hatchLinesB.map((h) => <line key={h.x} x1={h.x} y1={h.y1 + 4} x2={h.x} y2={h.y2 - 3} />)}
              </g>
              <rect x="163" y={bBot} width="3.6" height={(gY - bBot) * b} fill={INK} opacity="0.9" rx="0.5" />
            </g>
          )}
          {r > 0 && (
            <g opacity={r}>
              <rect x={bX - 3} y={bTop0 - 5 - roofDrop} width={bW + 6} height="5" fill={PURPLE} rx="1" />
              <rect x={aX - 3} y={aTop0 - 4 - roofDrop} width="21" height="4" fill={INK} rx="1" />
              <rect x="112" y={133 - roofDrop} width="28" height="3.2" fill={INK} rx="1" opacity="0.9" />
            </g>
          )}
          {gl > 0 && (
            <g opacity={gl}>
              <rect x={gxA} y={gyA} width={gwA} height={ghA} fill={TEAL_L} opacity="0.45" rx="1.5" />
              <rect x={gxA} y={gyA} width={gwA} height={ghA} fill="none" stroke={TEAL_G} strokeWidth="1.8" rx="1.5" />
              <line x1={gxA + gwA / 3} y1={gyA} x2={gxA + gwA / 3} y2={gyA + ghA} stroke={TEAL_G} strokeWidth="1.2" opacity="0.8" />
              <line x1={gxA + (2 * gwA) / 3} y1={gyA} x2={gxA + (2 * gwA) / 3} y2={gyA + ghA} stroke={TEAL_G} strokeWidth="1.2" opacity="0.8" />
              <rect x={gxB} y={gyB} width={gwB} height={ghB} fill={TEAL_L} opacity="0.45" rx="1.5" />
              <rect x={gxB} y={gyB} width={gwB} height={ghB} fill="none" stroke={TEAL_G} strokeWidth="1.8" rx="1.5" />
              {[0.25, 0.5, 0.75].map((k) => (
                <line key={k} x1={gxB + gwB * k} y1={gyB} x2={gxB + gwB * k} y2={gyB + ghB}
                  stroke={TEAL_G} strokeWidth="1.2" opacity="0.8" />
              ))}
            </g>
          )}
          {f > 0 && (
            <g opacity={f}>
              <rect x={dx} y={dy} width={dw} height={dh} fill={PAPER} opacity="0.94" rx="1" />
              <rect x={dx} y={dy} width={dw} height={dh} fill="none" stroke={INK} strokeWidth="1.3" rx="1" />
              <circle cx={dx + 11} cy={dy + 16} r="1.4" fill={PURPLE_D} />
              {scene && (
                <g>
                  <line x1="22" y1={gY} x2="22" y2="141" stroke={INK} strokeWidth="1.6" strokeLinecap="round" opacity="0.8" />
                  <circle cx="22" cy="130" r="12" fill={TEAL_L} opacity="0.4" />
                  <circle cx="22" cy="130" r="12" fill="none" stroke={TEAL_G} strokeWidth="1.3" />
                  <polygon points={quad([[30, 186], [118, 182], [140, 198], [50, 202]])} fill={TEAL_L} opacity="0.38" />
                  <polygon points={quad([[30, 186], [118, 182], [140, 198], [50, 202]])} fill="none" stroke={TEAL_G} strokeWidth="1.3" strokeLinejoin="round" />
                  <path d="M46 191 q7 -2 14 0 t14 0 t14 0" fill="none" stroke={TEAL_G} strokeWidth="1" opacity="0.55" />
                  <path d="M60 197 q7 -2 14 0 t14 0 t14 0" fill="none" stroke={TEAL_G} strokeWidth="1" opacity="0.45" />
                </g>
              )}
              <circle cx={bX + bW / 2} cy={bTop0 - 9} r={3 + 2 * f} fill={TEAL_G} />
              <circle cx={bX + bW / 2} cy={bTop0 - 9} r={7 + 10 * f} fill={TEAL_G} opacity={0.16 * f} />
            </g>
          )}
        </g>
      </svg>

      {label !== null && (
        <div style={{ fontFamily: "var(--brand-font-body, system-ui, sans-serif)", fontSize: 13, color: INK, fontWeight: 600, letterSpacing: 0.2 }}>
          {label ?? `${Math.round(p)}%`}
        </div>
      )}
    </div>
  );
}

/**
 * Indeterminate variant: auto-cycles the construction 0→100→0.
 * Drop-in replacement for spinners when we don't know real progress.
 *
 * Props:
 *   size            (default 180)   — px
 *   label           (default "A carregar…") — text below; pass null to hide
 *   durationMs      (default 4600)  — one full build cycle
 *   pauseAtEndMs    (default 700)   — hold at 100% before restarting
 */
function HouseLoaderAuto({
  size = 180,
  label = "A carregar…",
  durationMs = 4600,
  pauseAtEndMs = 700,
  scene = true,
}) {
  const [progress, setProgress] = useState(0);
  const raf = useRef(null);
  const timeout = useRef(null);

  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduce) {
      setProgress(100);
      return;
    }

    let cancelled = false;
    let start = null;

    const tick = (t) => {
      if (cancelled) return;
      if (start == null) start = t;
      const v = Math.min(100, ((t - start) / durationMs) * 100);
      setProgress(v);
      if (v < 100) {
        raf.current = requestAnimationFrame(tick);
      } else {
        timeout.current = setTimeout(() => {
          start = null;
          setProgress(0);
          raf.current = requestAnimationFrame(tick);
        }, pauseAtEndMs);
      }
    };

    raf.current = requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      if (raf.current) cancelAnimationFrame(raf.current);
      if (timeout.current) clearTimeout(timeout.current);
    };
  }, [durationMs, pauseAtEndMs]);

  return (
    <HouseLoader
      progress={progress}
      size={size}
      label={label}
      scene={scene}
    />
  );
}

/**
 * Zonamento loader (switch on): three stacked legend squares, square-edged like the
 * logo. No progress = indeterminate (lit in turn); a progress lights one per third.
 * prefers-reduced-motion keeps all three lit (ui_brand.css).
 */
export function ZonamentoLoader({ progress, size = 180, label }) {
  const auto = progress == null;
  const p = auto ? 0 : Math.max(0, Math.min(100, progress));
  const side = Math.max(10, Math.round(size / 10));
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={auto ? undefined : Math.round(p)}
      aria-label={label || "A carregar"}
      className={auto ? "zui-loader zui-loader--auto" : "zui-loader"}
      data-testid="mid-house-loader"
    >
      <div className="zui-loader__mark" style={{ gap: Math.round(side / 4) }} aria-hidden="true">
        {[1, 2, 3].map((n) => (
          <span key={n} className="zui-loader__sq" data-lit={!auto && p >= (n * 100) / 3 - 0.5}
            style={{ width: side, height: side }} />
        ))}
      </div>
      {label !== null && (
        <div className="zui-loader__label">{label ?? <span className="zui-num">{Math.round(p)}%</span>}</div>
      )}
    </div>
  );
}

function ZonamentoLoaderAuto({ size = 180, label = "A carregar…" }) {
  return <ZonamentoLoader size={size} label={label} />;
}

// NEW_BRAND is a build-time constant: each build ships one loader.
export const MiDHouseLoader = NEW_BRAND ? ZonamentoLoader : HouseLoader;
export const MiDHouseLoaderAuto = NEW_BRAND ? ZonamentoLoaderAuto : HouseLoaderAuto;

export default MiDHouseLoader;
